import type { GeoPoint } from "../config/amapChargingDemo";
import {
  LONGYAO_WAITING_DESTINATION,
  getWaitingRouteBand,
  type WaitingRouteBand,
  type WaitingRouteBandId,
  type WaitingRouteCandidate
} from "../config/amapWaitingRouteDemo";
import {
  fetchDrivingRouteGeometry,
  hasForbiddenDrivingManeuver,
  type RouteGeometry
} from "./amapClient";

export type TimedDetourPlanStatus = "loading" | "ready" | "fallback";

export interface TimedDetourPlan {
  geometry: RouteGeometry;
  bandId: WaitingRouteBandId;
  dataSource: "amap-live" | "amap-cache" | "amap-snapshot" | "amap-snapshot-cache";
  estimatedDurationSeconds: number;
  withinBand: boolean;
  status: TimedDetourPlanStatus;
  routeName: string;
  unavailableReason?: "amap-route-unavailable";
}

const MIN_LOOP_DURATION_SECONDS = 180;
const MIN_LOOP_DISTANCE_KM = 0.5;
const WAITING_ROUTE_CACHE_PREFIX = "geely-waiting-route-v2:";
const WAITING_ROUTE_CACHE_TTL_MS = 1000 * 60 * 60 * 24;
const WAITING_ROUTE_SNAPSHOT_CACHE_TTL_MS = WAITING_ROUTE_CACHE_TTL_MS;
const ENDPOINT_MAX_DISTANCE_M = 50;

interface CachedTimedDetourPlan {
  cachedAt: number;
  candidateId: string;
  plan: TimedDetourPlan;
}

const waitingRouteMemoryCache = new Map<string, CachedTimedDetourPlan>();
const waitingRouteInFlight = new Map<string, Promise<TimedDetourPlan>>();

export async function planTimedDetourRoute({
  bandId,
  origin,
  webServiceKey
}: {
  bandId: WaitingRouteBandId;
  origin: GeoPoint;
  webServiceKey: string;
}): Promise<TimedDetourPlan> {
  const band = getWaitingRouteBand(bandId);
  const cacheKey = createWaitingRouteCacheKey(band, origin);
  const cachedPlan = readCachedTimedDetourPlan(cacheKey, band, origin);

  if (cachedPlan) {
    return cachedPlan;
  }

  const inFlightPlan = waitingRouteInFlight.get(cacheKey);

  if (inFlightPlan) {
    return inFlightPlan;
  }

  const planningPromise = planFreshTimedDetourRoute({
    band,
    cacheKey,
    origin,
    webServiceKey
  }).catch(() => {
    const fallbackPlan: TimedDetourPlan = {
      ...createFallbackTimedDetourPlan({ bandId: band.id, origin }),
      unavailableReason: "amap-route-unavailable"
    };
    writeCachedTimedDetourPlan(cacheKey, band, fallbackPlan);
    return fallbackPlan;
  }).finally(() => {
    waitingRouteInFlight.delete(cacheKey);
  });

  waitingRouteInFlight.set(cacheKey, planningPromise);
  return planningPromise;
}

async function planFreshTimedDetourRoute({
  band,
  cacheKey,
  origin,
  webServiceKey
}: {
  band: WaitingRouteBand;
  cacheKey: string;
  origin: GeoPoint;
  webServiceKey: string;
}) {
  const geometry = await fetchLoopGeometry({
    candidate: band.route,
    origin,
    webServiceKey
  });
  const plan = createPlanFromGeometry({ band, geometry });

  if (
    !plan.withinBand ||
    hasPrematureDestinationPass(geometry.activePath) ||
    !hasValidRouteEndpoints(geometry.activePath, origin)
  ) {
    throw new Error("amap-route-unavailable");
  }

  writeCachedTimedDetourPlan(cacheKey, band, plan);
  return plan;
}

export function createFallbackTimedDetourPlan({
  bandId,
  origin,
  status = "fallback"
}: {
  bandId: WaitingRouteBandId;
  origin: GeoPoint;
  status?: Extract<TimedDetourPlanStatus, "loading" | "fallback">;
}): TimedDetourPlan {
  const band = getWaitingRouteBand(bandId);
  const candidate = band.route;
  const estimatedDurationSeconds = candidate.expectedDurationSeconds;
  const geometryStatus = status === "loading" ? "loading" : "fallback";
  const geometry: RouteGeometry = {
    activePath: candidate.snapshotPath.map((point) => ({ ...point })),
    summary: {
      distanceKm: candidate.snapshotDistanceKm,
      durationSeconds: estimatedDurationSeconds,
      durationMinutes: Math.max(1, Math.round(estimatedDurationSeconds / 60)),
      trafficLights: candidate.snapshotTrafficLights,
      tollsYuan: 0,
      waypointCount: candidate.waypoints.length,
      congestedDistanceKm: 0,
      trafficStatus: "unknown"
    },
    maneuvers: candidate.snapshotManeuvers.map((maneuver) => ({ ...maneuver })),
    waypoints: candidate.waypoints,
    source: "fallback",
    status: geometryStatus
  };

  return {
    geometry,
    bandId: band.id,
    dataSource: "amap-snapshot",
    estimatedDurationSeconds,
    withinBand: isDurationWithinBand(estimatedDurationSeconds, band),
    status,
    routeName: candidate.label
  };
}

function createPlanFromGeometry({
  band,
  geometry
}: {
  band: WaitingRouteBand;
  geometry: RouteGeometry;
}): TimedDetourPlan {
  const candidate = band.route;
  const estimatedDurationSeconds = getDurationSeconds(geometry, candidate.expectedDurationSeconds);

  return {
    geometry,
    bandId: band.id,
    dataSource: "amap-live",
    estimatedDurationSeconds,
    withinBand: isDurationWithinBand(estimatedDurationSeconds, band),
    status: "ready",
    routeName: candidate.label
  };
}

function createWaitingRouteCacheKey(band: WaitingRouteBand, origin: GeoPoint) {
  return [
    band.id,
    band.route.id,
    createWaitingRouteFingerprint(band),
    origin.lng.toFixed(5),
    origin.lat.toFixed(5)
  ].join(":");
}

function createWaitingRouteFingerprint(band: WaitingRouteBand) {
  const route = band.route;
  const fingerprintInput = [
    "strategy-10",
    "right-turn-only-v2",
    formatGeoPoint(LONGYAO_WAITING_DESTINATION.location),
    `${band.minMinutes}-${band.maxMinutes}`,
    String(route.expectedDurationSeconds),
    String(route.snapshotDistanceKm),
    String(route.snapshotTrafficLights),
    route.waypoints.map(formatGeoPoint).join(";"),
    route.snapshotPath.map(formatGeoPoint).join(";"),
    route.snapshotManeuvers
      .map((maneuver) => `${maneuver.action}/${maneuver.assistantAction}/${maneuver.instruction}`)
      .join(";")
  ].join("|");

  return hashFingerprint(fingerprintInput);
}

function hashFingerprint(value: string) {
  let hash = 2166136261;

  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }

  return (hash >>> 0).toString(36);
}

function formatGeoPoint(point: GeoPoint) {
  return `${point.lng.toFixed(6)},${point.lat.toFixed(6)}`;
}

function readCachedTimedDetourPlan(
  cacheKey: string,
  band: WaitingRouteBand,
  origin: GeoPoint
) {
  const memoryEntry = waitingRouteMemoryCache.get(cacheKey);

  if (isUsableCachedTimedDetourPlan(memoryEntry, band, origin)) {
    return markTimedDetourPlanAsCached(memoryEntry.plan);
  }

  waitingRouteMemoryCache.delete(cacheKey);

  if (typeof window === "undefined") {
    return null;
  }

  const storageKey = `${WAITING_ROUTE_CACHE_PREFIX}${cacheKey}`;

  try {
    const serializedEntry = window.localStorage.getItem(storageKey);

    if (!serializedEntry) {
      return null;
    }

    const storedEntry = JSON.parse(serializedEntry) as CachedTimedDetourPlan;

    if (!isUsableCachedTimedDetourPlan(storedEntry, band, origin)) {
      window.localStorage.removeItem(storageKey);
      return null;
    }

    waitingRouteMemoryCache.set(cacheKey, storedEntry);
    return markTimedDetourPlanAsCached(storedEntry.plan);
  } catch {
    return null;
  }
}

function writeCachedTimedDetourPlan(
  cacheKey: string,
  band: WaitingRouteBand,
  plan: TimedDetourPlan
) {
  const entry: CachedTimedDetourPlan = {
    cachedAt: Date.now(),
    candidateId: band.route.id,
    plan
  };
  waitingRouteMemoryCache.set(cacheKey, entry);

  if (typeof window === "undefined") {
    return;
  }

  try {
    window.localStorage.setItem(
      `${WAITING_ROUTE_CACHE_PREFIX}${cacheKey}`,
      JSON.stringify(entry)
    );
  } catch {
    // Storage can be unavailable in private browsing. The memory cache still
    // prevents duplicate calls for the current demo session.
  }
}

function isUsableCachedTimedDetourPlan(
  entry: CachedTimedDetourPlan | undefined,
  band: WaitingRouteBand,
  origin: GeoPoint
): entry is CachedTimedDetourPlan {
  if (
    !entry ||
    entry.candidateId !== band.route.id
  ) {
    return false;
  }

  const plan = entry.plan;
  const path = plan?.geometry?.activePath;
  const cacheTtlMs = plan?.dataSource === "amap-live"
    ? WAITING_ROUTE_CACHE_TTL_MS
    : WAITING_ROUTE_SNAPSHOT_CACHE_TTL_MS;
  const isLivePlan = plan?.status === "ready" && plan.geometry.source === "amap";
  const isSnapshotPlan =
    plan?.status === "fallback" &&
    plan.geometry.source === "fallback" &&
    plan.dataSource === "amap-snapshot";

  return Boolean(
    plan &&
    Date.now() - entry.cachedAt <= cacheTtlMs &&
    plan.bandId === band.id &&
    (isLivePlan || isSnapshotPlan) &&
    Array.isArray(path) &&
    path.length >= 4 &&
    path.every(isFiniteGeoPoint) &&
    isDurationWithinBand(plan.estimatedDurationSeconds, band) &&
    haveSameGeoPoints(plan.geometry.waypoints ?? [], band.route.waypoints) &&
    !hasForbiddenDrivingManeuver(plan.geometry.maneuvers ?? []) &&
    !hasPrematureDestinationPass(path) &&
    hasValidRouteEndpoints(path, origin)
  );
}

function haveSameGeoPoints(left: GeoPoint[], right: GeoPoint[]) {
  return left.length === right.length && left.every((point, index) =>
    formatGeoPoint(point) === formatGeoPoint(right[index])
  );
}

function markTimedDetourPlanAsCached(plan: TimedDetourPlan): TimedDetourPlan {
  return {
    ...plan,
    dataSource: plan.dataSource === "amap-snapshot"
      ? "amap-snapshot-cache"
      : "amap-cache"
  };
}

function isFiniteGeoPoint(point: GeoPoint) {
  return Number.isFinite(point?.lng) && Number.isFinite(point?.lat);
}

async function fetchLoopGeometry({
  candidate,
  origin,
  webServiceKey
}: {
  candidate: WaitingRouteCandidate;
  origin: GeoPoint;
  webServiceKey: string;
}) {
  if (!candidate.waypoints.length) {
    throw new Error("waiting-route-requires-waypoint");
  }

  const geometry = await fetchDrivingRouteGeometry({
    destination: LONGYAO_WAITING_DESTINATION.location,
    forbidLeftTurnsAndUTurns: true,
    origin,
    waypoints: candidate.waypoints,
    webServiceKey
  });

  const durationSeconds = getDurationSeconds(geometry, candidate.expectedDurationSeconds);
  const distanceKm = geometry.summary?.distanceKm ?? 0;

  if (hasForbiddenDrivingManeuver(geometry.maneuvers ?? [])) {
    throw new Error("amap-driving-forbidden-maneuver");
  }

  if (!hasAuditableDrivingManeuvers(geometry.maneuvers ?? [])) {
    throw new Error("amap-driving-maneuver-details-missing");
  }

  if (durationSeconds < MIN_LOOP_DURATION_SECONDS || distanceKm < MIN_LOOP_DISTANCE_KM) {
    throw new Error("amap-degenerate-loop-route");
  }

  return geometry;
}

function hasAuditableDrivingManeuvers(maneuvers: NonNullable<RouteGeometry["maneuvers"]>) {
  return maneuvers.length > 0 && maneuvers.every(
    (maneuver) => maneuver.instruction.trim().length > 0
  );
}

function hasValidRouteEndpoints(path: GeoPoint[], origin: GeoPoint) {
  return Boolean(
    path.length >= 2 &&
    getApproxDistanceM(path[0], origin) <= ENDPOINT_MAX_DISTANCE_M &&
    getApproxDistanceM(
      path[path.length - 1],
      LONGYAO_WAITING_DESTINATION.location
    ) <= ENDPOINT_MAX_DISTANCE_M
  );
}

function getDurationSeconds(geometry: RouteGeometry, fallbackSeconds: number) {
  return geometry.summary?.durationSeconds ??
    Math.max(60, Math.round((geometry.summary?.durationMinutes ?? fallbackSeconds / 60) * 60));
}

function isDurationWithinBand(durationSeconds: number, band: WaitingRouteBand) {
  return durationSeconds >= band.minMinutes * 60 && durationSeconds <= band.maxMinutes * 60;
}

function hasPrematureDestinationPass(path: GeoPoint[]) {
  let leftOriginArea = false;
  let reenteredDestinationArea = false;

  for (let index = 0; index < Math.max(0, path.length - 1); index += 1) {
    const distanceM = getApproxDistanceM(path[index], LONGYAO_WAITING_DESTINATION.location);

    // This scene starts and ends at the same metro entrance. The first departure
    // from the station is expected; only a later visit followed by another
    // departure counts as passing the pickup point too early.
    if (!leftOriginArea) {
      if (distanceM >= 120) {
        leftOriginArea = true;
      }
      continue;
    }

    if (distanceM <= 35) {
      reenteredDestinationArea = true;
      continue;
    }

    if (reenteredDestinationArea && distanceM >= 120) {
      return true;
    }
  }

  return false;
}

function getApproxDistanceM(from: GeoPoint, to: GeoPoint) {
  const earthRadiusM = 6371000;
  const fromLat = toRadians(from.lat);
  const toLat = toRadians(to.lat);
  const deltaLat = toRadians(to.lat - from.lat);
  const deltaLng = toRadians(to.lng - from.lng);
  const haversine =
    Math.sin(deltaLat / 2) ** 2 +
    Math.cos(fromLat) * Math.cos(toLat) * Math.sin(deltaLng / 2) ** 2;

  return earthRadiusM * 2 * Math.atan2(Math.sqrt(haversine), Math.sqrt(1 - haversine));
}

function toRadians(value: number) {
  return value * Math.PI / 180;
}
